import React, { useMemo, useCallback } from 'react';
import { ReactComponent as FilterOn } from 'assets/icons/ui-elements/small/filter-on-sm.svg';
import { ReactComponent as FilterOff } from 'assets/icons/ui-elements/small/filter-off-sm.svg';
import { ReactComponent as XIcon } from 'assets/icons/ui-elements/small/x-mark-sm.svg';
import { LkpLoading } from 'components';
import type { UseAgendaMobileReturn } from '../../../hooks/useAgendaMobile';
import { EventColorMapper, EventColorSet } from '../../../utils/eventColors';
import { MobileWeekCalendar } from './MobileWeekCalendar';
import { MobileDayCardList } from './MobileDayCardList';
import { AgendaMobileDayEmptyState } from './AgendaMobileDayEmptyState';
import { CalendarFilters } from './CalendarFilters';
import {
  MobileToolbarRow,
  MobileFiltersExpandable,
  MobileDayListSection,
  MobileLoadingOverlay,
  TypeFilterContainer,
  TypeButton,
  FiltersButtonContainer,
  FiltersButton,
} from '../styled';

interface AgendaMobileLayoutProps extends UseAgendaMobileReturn {
  onEventClick: (event: import('pages/Agenda/utils/eventUtils').CalendarEvent, colorSet: EventColorSet) => void;
}

export const AgendaMobileLayout: React.FC<AgendaMobileLayoutProps> = props => {
  const {
    currentDate,
    weekDays,
    navigate,
    selectedDate,
    setSelectedDate,
    events,
    eventsForSelectedDay,
    horasPrevistasSelectedDay,
    type,
    setType,
    openFilters,
    setOpenFilters,
    hasActiveFilters,
    clearAllFilters,
    planosSelectionLabel,
    executoresSetoresSelectionLabel,
    ambientesEquipamentosSelectionLabel,
    maintenanceUnitsOptions,
    maintenanceUnitsValues,
    handleMaintenanceUnitsChange,
    setExecutoresSetoresModalOpen,
    setAmbientesEquipamentosModalOpen,
    setPlanosModalOpen,
    isLoadingEvents,
    onEventClick,
  } = props;

  const colorMapper = useMemo(() => new EventColorMapper(), []);
  const getColorSet = useCallback(
    (event: { title: string }) => colorMapper.getColorForEvent(event.title),
    [colorMapper],
  );

  return (
    <div data-testid="agenda-mobile-layout" style={{ position: 'relative' }}>
      {isLoadingEvents && (
        <MobileLoadingOverlay>
          <LkpLoading />
        </MobileLoadingOverlay>
      )}
      <MobileWeekCalendar
        currentDate={currentDate}
        weekDays={weekDays}
        selectedDate={selectedDate}
        onSelectDay={setSelectedDate}
        onNavigate={navigate}
        eventsByDate={events}
      />
      <MobileToolbarRow>
        <TypeFilterContainer>
          <TypeButton isActive={type === 'pendentes'} onClick={() => setType('pendentes')} isRight={false}>
            Pendentes
          </TypeButton>
          <TypeButton isActive={type === 'baixadas'} onClick={() => setType('baixadas')} isRight>
            Baixadas
          </TypeButton>
        </TypeFilterContainer>
        <FiltersButtonContainer>
          <FiltersButton type="button" onClick={() => setOpenFilters(!openFilters)} aria-expanded={openFilters}>
            {openFilters ? <FilterOff /> : <FilterOn />}
            Filtros
          </FiltersButton>
          {hasActiveFilters && (
            <FiltersButton onClick={clearAllFilters}>
              <XIcon />
              Limpar Filtros
            </FiltersButton>
          )}
        </FiltersButtonContainer>
      </MobileToolbarRow>
      {openFilters && (
        <MobileFiltersExpandable>
          <CalendarFilters
            type={type}
            onTypeChange={setType}
            onOpenExecutoresModal={() => setExecutoresSetoresModalOpen(true)}
            onOpenAmbientesModal={() => setAmbientesEquipamentosModalOpen(true)}
            onOpenPlanosModal={() => setPlanosModalOpen(true)}
            planosSelectionLabel={planosSelectionLabel}
            executoresSetoresSelectionLabel={executoresSetoresSelectionLabel}
            ambientesEquipamentosSelectionLabel={ambientesEquipamentosSelectionLabel}
            maintenanceUnitsOptions={maintenanceUnitsOptions}
            maintenanceUnitsValues={maintenanceUnitsValues}
            onMaintenanceUnitsChange={handleMaintenanceUnitsChange}
            hideTypeTabs={true}
          />
        </MobileFiltersExpandable>
      )}
      <MobileDayListSection>
        {!isLoadingEvents && eventsForSelectedDay.length === 0 ? (
          <AgendaMobileDayEmptyState />
        ) : (
          <MobileDayCardList
            events={eventsForSelectedDay}
            getColorSet={getColorSet}
            isCompletedView={type === 'baixadas'}
            horasPrevistasLabel={horasPrevistasSelectedDay}
            onEventClick={onEventClick}
          />
        )}
      </MobileDayListSection>
    </div>
  );
};
